interface OffsetPage<T> {
  items: T[]
  offset: number
  total: number
}

interface FetchAllPagesOptions<T, P extends OffsetPage<T>> {
  fetchPage: (offset: number) => Promise<P>
  /** A circuit breaker, not an expected ceiling. */
  maxPages: number
  /** The error thrown when paging ends before every item is in hand. */
  incompleteError: (received: number, total: number) => Error
  /** Return true on the first page to stop and return no items. */
  isEmptyResult?: (firstPage: P) => boolean
}

/**
 * Pages through an offset-paginated list and returns every item. Throws
 * instead of returning a short list when the server stalls, returns an empty
 * page early, or the page cap is reached first.
 */
export const fetchAllPages = async <T, P extends OffsetPage<T>>({
  fetchPage,
  incompleteError,
  isEmptyResult,
  maxPages,
}: FetchAllPagesOptions<T, P>): Promise<T[]> => {
  const items: T[] = []
  let offset = 0
  let total = 0

  for (let pageNumber = 0; pageNumber < maxPages; pageNumber += 1) {
    const page = await fetchPage(offset)

    if (pageNumber === 0 && isEmptyResult?.(page)) {
      return []
    }

    if (page.items.length === 0) {
      total = page.total
      break
    }

    const nextOffset = page.offset + page.items.length
    if (nextOffset <= offset) {
      throw incompleteError(items.length, total)
    }

    total = page.total
    items.push(...page.items)

    if (items.length >= page.total) {
      break
    }

    offset = nextOffset
  }

  if (items.length < total) {
    throw incompleteError(items.length, total)
  }

  return items
}
